import createRaf from "@solid-primitives/raf";
import { createSignal, onCleanup, onMount } from "solid-js";
import { createStore } from "solid-js/store";

import { game } from "~/js/game";
import { Pages } from "~/js/pages";
import { createPage } from "~/js/utils";
import { getPlayerLevel, numToValuta } from "~/script";
import { user } from "~/userStore";

const clickFactor = 0.1;
const targetMovement = 30;

function Shooter() {
	const [autofire, setAutofire] = createSignal(false);
	const [crosshairState, setCrosshairState] = createStore({ x: 0, y: 0, hidden: true });
	const [multiplier, setMultiplier] = createSignal(1);
	const [multiplierOpacity, setMultiplierOpacity] = createSignal(0);
	const [multiplierFontSize, setMultiplierFontSize] = createSignal(30);
	const [transition, setTransition] = createSignal("0.1s");
	let canvas: HTMLCanvasElement | undefined;
	let canvasContext: CanvasRenderingContext2D | undefined;

	const targetState: { x: number; y: number; radius: number; hits: { x: number; y: number; dist: number }[] } = {
		x: 0,
		y: 0,
		radius: 50,
		hits: [],
	};
	let multiplierFadeTime = 0;

	function showFloatingText(text: string) {
		if (!canvas) return;
		const overlayElement = document.body.q({
			style: {
				position: "fixed",
				pointerEvents: "none",
				textShadow: "0 1px #000",
			},
		});
		overlayElement.innerText = text;
		const o = canvas.getBoundingClientRect();
		const xPos = o.x + o.width / 2 + crosshairState.x - overlayElement.offsetWidth / 2 - 40 + Math.random() * 80;
		const yPos = o.y + o.height / 2 + crosshairState.y - overlayElement.offsetHeight - Math.random() * 40;
		overlayElement.style.left = `${xPos}px`;
		overlayElement.style.top = `${yPos}px`;
		overlayElement.style.transition = "0.3s ease-out";

		setTimeout(() => {
			overlayElement.style.left = `${xPos + (Math.random() - 0.5) * 30}px`;
			overlayElement.style.top = `${yPos - 35}px`;
			overlayElement.style.opacity = "0";
		}, 100);

		overlayElement.addEventListener(
			"transitionend",
			() => {
				if (overlayElement.parentElement) overlayElement.parentElement.removeChild(overlayElement);
			},
			{ once: true },
		);
	}

	function handleShoot(pos: { x: number; y: number }) {
		// positive = inside target (higher → closer to center), 0 = on border, negative = outside
		const clickDistance = -(Math.sqrt((pos.x - targetState.x) ** 2 + (pos.y - targetState.y) ** 2) - targetState.radius);

		if (clickDistance > -10)
			targetState.hits.push({ x: pos.x - targetState.x, y: pos.y - targetState.y, dist: Math.max(0, clickDistance) });
		if (clickDistance < 0) {
			setMultiplier(1);
		} else {
			setMultiplier((prev) => Math.min(prev + 0.01, 5));
		}

		if (clickDistance > -10) {
			const opacity = 0.5 + Math.min(0.5, (multiplier() - 1) / 3);
			const initialFontSize = 35 + Math.min(60, (multiplier() - 1) * 30);
			const finalFontSize = 30 + Math.min(60, (multiplier() - 1) * 30);

			setTransition("0s");
			setMultiplierOpacity(opacity);
			setMultiplierFontSize(initialFontSize);
			multiplierFadeTime = performance.now() + 1e3;
			setTimeout(() => {
				setTransition("0.1s");
				setMultiplierFontSize(finalFontSize);
			}, 0);

			targetState.x += (Math.random() - (targetState.x > 0 ? 0.55 : 0.45)) * targetMovement * (1 + multiplier() / 25);
			targetState.y += (Math.random() - (targetState.y > 0 ? 0.55 : 0.45)) * targetMovement * (1 + multiplier() / 25);

			const avgDistance =
				targetState.hits
					.slice(-20)
					.map((t) => t.dist)
					.reduce((t, e) => t + e) /
				Math.min(targetState.hits.length, 20) /
				50;

			const clickImpactScore =
				(Math.max(0, Math.max(0.1, avgDistance) * (clickDistance > 0 ? clickDistance : 0) * (100 / targetState.radius) * clickFactor) *
					multiplier()) /
				10;

			const cashPerClick = clickImpactScore * user.upgrades.perClick * user.upgrades.perClickMul * (1 + getPlayerLevel().level * 0.05);
			const xpPerClick = clickImpactScore * user.upgrades.perClickXp * user.upgrades.xpmul * user.upgrades.xpMulMul;
			user.money += cashPerClick;
			user.xp += xpPerClick;
			showFloatingText(`+${numToValuta(cashPerClick, 2)}`);
			showFloatingText(`+${xpPerClick.toFixed(0)} xp`);
		}

		game.emit("update", true);
	}
	let lastAutofireTime = 0;
	function renderCanvas() {
		if (!canvas || !canvasContext) return;
		const currentTime = performance.now();

		canvas.width = pageRoot.offsetWidth;
		canvas.height = pageRoot.offsetHeight;

		canvasContext.translate(Math.floor(canvas.width / 2), Math.floor(canvas.height / 2));

		for (let ringNum = 0; ringNum < 5; ringNum++) {
			canvasContext.beginPath();
			canvasContext.arc(targetState.x, targetState.y, targetState.radius - (targetState.radius / 5) * ringNum, 0, Math.PI * 2);
			canvasContext.strokeStyle = "#fff8";
			canvasContext.stroke();
		}

		// draw crosshair
		canvasContext.beginPath();
		canvasContext.moveTo(targetState.x - 10, targetState.y);
		canvasContext.lineTo(targetState.x + 10, targetState.y);
		canvasContext.moveTo(targetState.x, targetState.y - 10);
		canvasContext.lineTo(targetState.x, targetState.y + 10);
		canvasContext.strokeStyle = "#fff8";
		canvasContext.stroke();
		if (targetState.hits.length > 200) {
			targetState.hits = targetState.hits.slice(-100);
		}
		for (const hitmarker of targetState.hits) {
			canvasContext.beginPath();
			canvasContext.moveTo(targetState.x + hitmarker.x - 3, targetState.y + hitmarker.y - 3);
			canvasContext.lineTo(targetState.x + hitmarker.x + 3, targetState.y + hitmarker.y + 3);
			canvasContext.moveTo(targetState.x + hitmarker.x - 3, targetState.y + hitmarker.y + 3);
			canvasContext.lineTo(targetState.x + hitmarker.x + 3, targetState.y + hitmarker.y - 3);
			canvasContext.strokeStyle = "#fff8";
			canvasContext.stroke();
		}

		if (!crosshairState.hidden) {
			// TODO: make this a hardware cursor
			canvasContext.beginPath();
			canvasContext.rect(Math.floor(crosshairState.x - 10), Math.floor(crosshairState.y), 21, 1);
			canvasContext.rect(Math.floor(crosshairState.x), Math.floor(crosshairState.y) - 10, 1, 21);
			canvasContext.fillStyle = "#fff";
			canvasContext.strokeStyle = "#000";
			canvasContext.stroke();
			canvasContext.fill();
		}
		canvasContext.translate(-canvas.width / 2, -canvas.height / 2);

		if (autofire() && lastAutofireTime + 100 < currentTime) {
			lastAutofireTime = currentTime;
			handleShoot(crosshairState);
		}

		if (multiplierFadeTime < currentTime) {
			setMultiplierOpacity(0);
		}
	}
	const [, start] = createRaf(renderCanvas);
	function spaceKeyListener(e: KeyboardEvent) {
		if (document.activeElement?.tagName === "INPUT") return;
		if (Pages.currentPage === "shooter" && e.code === "Space" && !e.repeat) {
			e.preventDefault();
			setAutofire((prev) => !prev);
		}
	}
	onMount(() => {
		if (!canvas) return;
		canvasContext = canvas.getContext("2d")!;
		start();
		canvas.onmousemove = (t) => {
			setCrosshairState({
				x: t.offsetX - canvas!.width / 2,
				y: t.offsetY - canvas!.height / 2,
				hidden: false,
			});
		};
		canvas.onmouseleave = () => {
			setCrosshairState("hidden", true);
		};
		canvas.onmousedown = (t) => {
			handleShoot({ x: t.offsetX - canvas!.width / 2, y: t.offsetY - canvas!.height / 2 });
		};

		window.addEventListener("keydown", spaceKeyListener);
	});

	onCleanup(() => {
		window.removeEventListener("keydown", spaceKeyListener);
	});
	return (
		<>
			<canvas
				ref={(el) => {
					canvas = el;
				}}
				style={{ cursor: "none" }}
			></canvas>
			<div
				style={{
					position: "absolute",
					left: "0",
					top: "30px",
					right: "0",
					display: "flex",
					"justify-content": "center",
				}}
			>
				{/** biome-ignore lint/a11y/noStaticElementInteractions: later */}
				{/** biome-ignore lint/a11y/useKeyWithClickEvents: later */}
				<div
					class="button auto-fire-button"
					classList={{ "auto-fire-enabled": autofire() }}
					data-tip="Use <kbd>Space</kbd> to toggle"
					data-tipposition="center"
					onClick={() => setAutofire(!autofire())}
				>
					{autofire() ? "Auto Firing" : "Auto Fire"}
				</div>
				{/** biome-ignore lint/a11y/noStaticElementInteractions: fart */}
				{/** biome-ignore lint/a11y/useKeyWithClickEvents: fart*/}
				<div
					class="button auto-fire-button"
					data-tip="Center target and clear hitmarkers"
					data-tipposition="center"
					onClick={() => {
						Object.assign(targetState, { x: 0, y: 0, radius: 50, hits: [] });
					}}
				>
					Reset Target
				</div>
			</div>
			<div
				style={{
					position: "absolute",
					left: "0",
					bottom: "60px",
					right: "0",
					display: "flex",
					"justify-content": "center",
					"pointer-events": "none",
					color: "#888",
				}}
			>
				<p>
					Earn money by hitting the center of the target.
					<br />
					The more accurate you are, the more you earn.
				</p>
			</div>
			<div
				style={{
					position: "absolute",
					left: "0",
					top: "200px",
					right: "0",
					"pointer-events": "none",
					"text-align": "center",
					color: "#f44",
					"font-size": `${multiplierFontSize()}px`,
					opacity: multiplierOpacity(),
					transition: transition(),
				}}
			>
				x{multiplier().toFixed(2)}
			</div>
		</>
	);
}

const pageRoot = createPage(() => <Shooter />, "shooter");
